import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(1),
    textAlign: 'center',
  },
  headerTitle: {
    fontWeight: 600,
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
    transition: 'box-shadow 0.3s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  },
  question: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  answer: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  list: {
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  listItem: {
    marginBottom: theme.spacing(0.5),
  },
  subSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
}));

const Faq = () => {
  const classes = useStyles();

  // Track page view in Clarity
  useEffect(() => {
    if (window.clarity) {
      window.clarity('event', 'page_viewed');
    }
  }, []);

  const faqs = [
    {
      q: "What does this monitoring system do?",
      a: "Our Industrial IoT platform provides 24/7 real-time monitoring of temperature, energy consumption, water flow, equipment status, and utility systems. It helps you track performance, prevent failures, and improve operational efficiency.",
    },
    {
      q: "What parameters are monitored?",
      a: (
        <div>
          The system can monitor:
          <ul className={classes.list}>
            <li className={classes.listItem}>Temperature (e.g., cold rooms)</li>
            <li className={classes.listItem}>Energy consumption (kWh)</li>
            <li className={classes.listItem}>Power factor</li>
            <li className={classes.listItem}>Water flow</li>
            <li className={classes.listItem}>Compressor, fan, and motor status</li>
            <li className={classes.listItem}>Grid and generator status</li>
            <li className={classes.listItem}>Equipment availability (%)</li>
          </ul>
        </div>
      ),
    },
    {
      q: "What do the status mean?",
      a: (
        <div>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              <strong>Normal</strong> – Value is within configured limits
            </li>
            <li className={classes.listItem}>
              <strong>Critical</strong> – Value has crossed the defined threshold
            </li>
            <li className={classes.listItem}>
              <strong>Sensor Malfunction</strong> – No data, communication failure, or abnormal sensor behavior
            </li>
          </ul>
        </div>
      ),
    },
    {
      q: "How is temperature monitored?",
      a: (
        <div>
          <p>Each temperature sensor is configured with limits:</p>
          <ul className={classes.list}>
            <li className={classes.listItem}>Min Range: -18.0°C</li>
            <li className={classes.listItem}>Max Range: 0.0°C</li>
          </ul>
          <p>If the temperature goes outside this range, a critical alert is generated.</p>
          <p>The Info (i) button shows:</p>
          <ul className={classes.list}>
            <li className={classes.listItem}>Configured limits</li>
            <li className={classes.listItem}>Current reading</li>
            <li className={classes.listItem}>Sensor health status</li>
          </ul>
        </div>
      ),
    },
    // Energy Meter FAQs
    {
      q: "What does 'Last Hour Consumption' mean for energy meter?",
      a: "It shows the total electrical energy consumed in the previous 60 minutes, measured in kilowatt-hours (kWh). This helps operators monitor short-term power usage and detect sudden load changes.",
    },
    {
      q: "What is 'Previous Day Consumption' for energy meter?",
      a: "This value represents the total electrical energy consumed during the previous calendar day (24 hours). It is useful for daily energy analysis and operational reporting.",
    },
    {
      q: "What is 'Average Power Factor'?",
      a: "Power Factor indicates how efficiently electrical power is being used. It is the ratio between real power and apparent power, expressed as a value between 0 and 1.",
    },
    {
      q: "What is a good Power Factor value?",
      a: "A power factor close to 1.0 is considered optimal. Most industrial facilities maintain a value above 0.90 to ensure efficient operation and avoid utility penalties.",
    },
    {
      q: "Why is Power Factor important in industrial plants?",
      a: (
        <div>
          Maintaining a good power factor:
          <ul className={classes.list}>
            <li className={classes.listItem}>Improves electrical efficiency</li>
            <li className={classes.listItem}>Reduces energy losses</li>
            <li className={classes.listItem}>Helps avoid penalties from electricity providers</li>
          </ul>
        </div>
      ),
    },
    {
      q: "Why might energy consumption change suddenly?",
      a: (
        <div>
          Sudden changes can occur due to:
          <ul className={classes.list}>
            <li className={classes.listItem}>Equipment startup or shutdown</li>
            <li className={classes.listItem}>High load machines operating</li>
            <li className={classes.listItem}>Maintenance activities</li>
            <li className={classes.listItem}>Production changes</li>
          </ul>
        </div>
      ),
    },
    // Flow Meter FAQs
    {
      q: "What does the Flow Meter measure?",
      a: "The flow meter measures the volume of liquid passing through a pipeline, typically displayed in litres (L) or kilolitres (KL).",
    },
    {
      q: "What does 'Last Hour' flow represent?",
      a: "This shows the total volume of fluid that passed through the pipe in the previous hour.",
    },
    {
      q: "What does 'Previous Day' flow mean?",
      a: "This value represents the total fluid volume that flowed through the system during the previous day.",
    },
    {
      q: "What is 'Last Month' flow?",
      a: "This indicates the cumulative fluid usage during the previous month, helping with consumption tracking and reporting.",
    },
    {
      q: "Why might the flow value show zero?",
      a: (
        <div>
          A zero reading may occur when:
          <ul className={classes.list}>
            <li className={classes.listItem}>The pump is not operating</li>
            <li className={classes.listItem}>No fluid is passing through the pipe</li>
            <li className={classes.listItem}>The process line is inactive</li>
            <li className={classes.listItem}>Maintenance or shutdown is in progress</li>
          </ul>
        </div>
      ),
    },
    {
      q: "How is flow data useful in industrial monitoring?",
      a: (
        <div>
          Flow monitoring helps:
          <ul className={classes.list}>
            <li className={classes.listItem}>Track water or liquid usage</li>
            <li className={classes.listItem}>Detect leakages or abnormal consumption</li>
            <li className={classes.listItem}>Optimize process efficiency</li>
            <li className={classes.listItem}>Support production reporting</li>
          </ul>
        </div>
      ),
    },
    {
      q: "What information is available for water flow?",
      a: (
        <div>
          <p>The Flowmeter Info panel shows:</p>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              Last Hour Consumption (e.g., 09:00–10:00)
            </li>
            <li className={classes.listItem}>
              Previous Day Consumption (e.g., Yesterday 06:00 AM – Today 06:00 AM)
            </li>
            <li className={classes.listItem}>Last Month Consumption</li>
          </ul>
          <p>This helps track usage trends and detect abnormal consumption.</p>
        </div>
      ),
    },
    {
      q: "What information is available for energy monitoring?",
      a: (
        <div>
          <p>The Energy Meter Info panel shows:</p>
          <ul className={classes.list}>
            <li className={classes.listItem}>Last Hour Consumption</li>
            <li className={classes.listItem}>Previous Day Consumption</li>
            <li className={classes.listItem}>Average Power Factor</li>
          </ul>
          <p>This helps monitor electrical load and energy efficiency.</p>
        </div>
      ),
    },
    {
      q: "How are alerts generated?",
      a: "Alerts are automatically triggered when a parameter exceeds configured limits. Alerts can be viewed on the dashboard notifications and in users' email.",
    },
  ];

  return (
    <>
      {/* Custom header instead of PageHeader */}
      <Box className={classes.header}>
        <Typography variant="h4" component="h1" className={classes.headerTitle}>
          Frequently Asked Questions (FAQ)
        </Typography>
        <Typography variant="subtitle1">
          Find answers to common questions about our monitoring system
        </Typography>
      </Box>

      <Container maxWidth="lg" className={classes.root}>
        <Grid container spacing={3}>
          {faqs.map((faq, index) => (
            <Grid item xs={12} key={index}>
              <Paper className={classes.paper} elevation={2}>
                <Typography variant="h6" component="h2" className={classes.question}>
                  {faq.q}
                </Typography>
                <div className={classes.answer}>
                  {faq.a}
                </div>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export default Faq;
